// Catálogo público desde el CDN (R2 en <DOMINIO_CDN>): 0 lecturas de Firestore.
// Primero el puntero (no-cache) y luego el contenido versionado. Si falta la URL,
// el puntero no existe (catálogo oculto o sin generar) o algo falla, se usa el
// camino de siempre: listeners de Firestore. La página nunca depende del CDN.
// El respaldo (y con él el SDK de Firestore) se descarga solo si el CDN falla.
import { catalogoCoherente, modoCobroCatalogo, ordenarProductosCatalogo, validarIdCatalogo } from '../dominio/logicaCatalogoPublico';
import { origenPublico } from '../utilidades/origenPublico';

const TIEMPO_MAXIMO_MS = 4000;
// Dominio propio del bucket del catálogo (no es una credencial). La variable lo
// cambia; definida vacía desactiva el CDN y la página usa solo Firestore.
const CDN_POR_DEFECTO = 'https://catalogo.allgym.app';
const baseCdn = () => {
  const base = String(process.env.REACT_APP_CATALOGO_JSON_URL ?? CDN_POR_DEFECTO).trim().replace(/\/+$/, '');
  return /^https:\/\/[^/?#]+$/.test(base) ? base : '';
};

const pedirJson = async (url, cache, senal) => {
  const respuesta = await fetch(url, { cache, signal: senal, credentials: 'omit' });
  if (!respuesta.ok) throw Object.assign(new Error(`CDN ${respuesta.status}`), { status: respuesta.status });
  return respuesta.json();
};

/** Valida el JSON con las mismas reglas que la vista en vivo; null si no sirve. */
/**
 * Enlace para compartir la tienda: la página del CDN ({base}/tienda/{gymId}) lleva el
 * nombre y el logo del gym en la vista previa de WhatsApp/Facebook y abre la
 * tienda al instante. Sin CDN, el enlace directo de la app.
 */
export const enlaceCompartirTienda = (gymId, producto = '') => {
  const base = baseCdn();
  const id = encodeURIComponent(gymId);
  const hash = producto ? `#p-${encodeURIComponent(producto)}` : '';
  return base ? `${base}/tienda/${id}${hash}` : `${origenPublico()}/catalogo/${id}${hash}`;
};

export const catalogoDesdeJson = (gymId, json) => {
  if (json?.formato !== 1 || json.gymId !== gymId || !Array.isArray(json.productos)) return null;
  const productos = json.productos.map(p => ({ ...p, gymId, version: json.version }));
  const cabecera = { ...json, publicado: true, productoIds: productos.map(p => p.id) };
  delete cabecera.productos; delete cabecera.formato;
  if (!catalogoCoherente(cabecera, productos)) return null;
  return { ...cabecera, modoCobro: modoCobroCatalogo(cabecera), productos: ordenarProductosCatalogo(productos) };
};

/** Catálogo desde el CDN o null (sin URL configurada, oculto o con error). */
export const leerCatalogoCdn = async (gymId, senal) => {
  const base = baseCdn();
  if (!base) return null;
  const id = validarIdCatalogo(gymId);
  const puntero = await pedirJson(`${base}/catalogos/${id}/publico/actual.json`, 'no-cache', senal);
  const clave = String(puntero?.clave || '');
  if (puntero?.gymId !== id || !new RegExp(`^catalogos/${id}/publico/v/[A-Za-z0-9_-]{1,120}-[a-f0-9]{16}\\.json$`).test(clave)) return null;
  return catalogoDesdeJson(id, await pedirJson(`${base}/${clave}`, 'default', senal));
};

/**
 * Misma firma que escucharCatalogoPublico. Con CDN entrega el catálogo una vez
 * (sin listeners); volver a la pestaña tras la pausa lo vuelve a pedir. Si el CDN
 * no responde con un catálogo válido, abre los listeners de Firestore.
 */
export const observarCatalogoPublico = (gymId, recibir, error) => {
  let vigente = true, cancelarFirestore = null;
  const control = typeof AbortController === 'function' ? new AbortController() : null;
  const limite = setTimeout(() => control?.abort(), TIEMPO_MAXIMO_MS);
  let pidioFirestore = false;
  const usarFirestore = () => {
    if (!vigente || pidioFirestore) return;
    pidioFirestore = true;
    import('./catalogoPublicoLectura')
      .then(({ escucharCatalogoPublico }) => { if (vigente) cancelarFirestore = escucharCatalogoPublico(gymId, recibir, error); })
      .catch(e => { if (vigente) error(e); });
  };
  leerCatalogoCdn(gymId, control?.signal)
    .then(catalogo => { if (!vigente) return; if (catalogo) recibir(catalogo); else usarFirestore(); })
    .catch(() => usarFirestore())
    .finally(() => clearTimeout(limite));
  return () => { vigente = false; clearTimeout(limite); control?.abort(); cancelarFirestore?.(); };
};
